/**
 * Иконочная кнопка (§2.3 вариант `ghost`, §13 зоны нажатия).
 *
 * Отличается от <Button> ровно двумя вещами: она квадратная и у неё нет
 * подписи — поэтому `label` обязателен и уходит в `aria-label` и в тултип.
 * Внутри строки таблицы (28 px) зона нажатия 24×24: `inTable`.
 */
import { forwardRef } from 'react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import './IconButton.css'

export type IconButtonVariant = 'ghost' | 'secondary' | 'primary' | 'danger-ghost'

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'aria-label'> {
  /** Доступное имя: обязательно, иконка без подписи иначе молчит (§13). */
  label: string
  icon: ReactNode
  variant?: IconButtonVariant
  /** s 24 · m 32 (по умолчанию) · l 40. */
  size?: 's' | 'm' | 'l'
  /** Кнопка внутри строки таблицы: зона 24×24 (§13). */
  inTable?: boolean
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    label,
    icon,
    variant = 'ghost',
    size = 'm',
    inTable = false,
    disabled = false,
    className,
    onClick,
    type = 'button',
    ...rest
  },
  ref,
) {
  const classes = ['icon-btn', `icon-btn--${variant}`, `icon-btn--${size}`]
  if (inTable) classes.push('icon-btn--table')
  if (className) classes.push(className)

  return (
    <button
      ref={ref}
      type={type}
      className={classes.join(' ')}
      aria-label={label}
      title={label}
      aria-disabled={disabled || undefined}
      onClick={(event) => {
        if (disabled) {
          event.preventDefault()
          return
        }
        onClick?.(event)
      }}
      {...rest}
    >
      {icon}
    </button>
  )
})
